/**
 * App.jsx — Artizan Root Component
 * Wires Auth.jsx → ArtistryConnect-Merged.jsx with real Supabase session
 *
 * Routing for unauthenticated users:
 *   /          → PublicLanding (marketing site)
 *   /login     → Auth (existing login form)
 *   anything else → redirect to /
 *
 * Logged-in behavior unchanged.
 */
 
import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import Auth from './Auth';
import ArtistryConnect from './ArtistryConnect-Merged';
import PublicLanding from './PublicLanding';
 
export default function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
 
  // — On mount: check for existing session —
  useEffect(() => {
    // Get current session (handles page refresh — user stays logged in)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });
 
    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          await fetchProfile(session.user.id);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          setLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
        }
      }
    );
 
    // Cleanup listener on unmount
    return () => subscription.unsubscribe();
  }, []);
 
  // — Fetch user profile from Supabase —
  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
 
      if (error) {
        console.error('Profile fetch error:', error.message);
        // Profile may not exist yet — that's ok for brand new signups
        // Auth.jsx creates the profile row on signup
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error('Unexpected error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };
 
  // — Called by Auth.jsx after successful login or signup —
  const handleAuth = async (authedUser) => {
    setUser(authedUser);
    await fetchProfile(authedUser.id);
  };
 
  // — Called when user signs out from within the app —
  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error.message);
    }
    // onAuthStateChange listener above handles clearing state
  };
 
  // — Loading state — shown while checking session on page load —
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#06060d',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 16,
        fontFamily: "'DM Sans', sans-serif",
      }}>
        <div style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #d4a853, #f0c878)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'Georgia, serif',
          fontWeight: 700,
          fontSize: 26,
          color: '#06060d',
        }}>
          A
        </div>
        <div style={{ color: '#9890b0', fontSize: 14 }}>Loading...</div>
      </div>
    );
  }
 
  // — Not logged in: route between PublicLanding (/) and Auth (/login) —
  if (!user) {
    const path = window.location.pathname;
 
    // /login → existing Auth form
    if (path === '/login') {
      return <Auth onAuth={handleAuth} />;
    }
 
    // / → PublicLanding (the new marketing site)
    // Any other path → also PublicLanding (will become a 404 page in a later iteration)
    return <PublicLanding />;
  }
 
  // — Logged in — show main app —
  return (
    <ArtistryConnect
      user={user}
      profile={profile}
      onSignOut={handleSignOut}
      onCreatePost={async (postData) => {
        // Wire post creation to real Supabase
        const { error } = await supabase
          .from('posts')
          .insert({
            user_id: user.id,
            content: postData.content,
            media_urls: postData.mediaUrls || [],
            ember_expires_at: postData.emberTTL || null,
          });
        if (error) console.error('Post creation error:', error.message);
      }}
    />
  );
}
